import {
  InputProps,
  Popover,
  PopoverAnchor,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useCallback, useRef } from "react";
import "react-day-picker/style.css";
import { useCloseWhenOutsideViewport } from "../../../hooks/useCloseWhenOutsideViewport";
import { useCurrentBreakpoint } from "../../../hooks/useCurrentBreakpoint";
import { useOverlayPortalContainer } from "../../modals/OverlayPortalContainerContext";
import { DatePickerInput } from "./DatePickerInput";
import { DatePickerPopoverContent } from "./DatePickerPopoverContent";
import { DatePreset } from "./DatePickerPresetsDropdown";

export type DatePickerProps = {
  /**
   * The selected date value.
   *
   * If you are supplying this from a React Hook Form, ensure you convert the
   * empty string "" into undefined
   */
  value: Date | undefined;

  /**
   * Callback function for when the selected date value changed
   */
  onChange: (date: Date | undefined) => void;

  /**
   * The earliest date allowed in the date picker.
   */
  earliestDate?: Date;

  /**
   * The latest date allowed in the date picker.
   */
  latestDate?: Date;

  /**
   * Show a time input
   */
  showTime: boolean;

  presetTitle?: string;
  presetDates?: DatePreset[];

  /**
   * IANA Time Zone.
   * https://daypicker.dev/localization/setting-time-zone
   */
  timeZone: string;

  inputProps?: InputProps;

  isDisabled?: boolean;

  isRequired?: boolean;

  isInvalid?: boolean;
};

/**
 * Date picker component with popover content for selecting date and time.
 *
 * `onChange` will be called with the selected Date.
 *
 * If `showTime` is false, then the start of the day in the configured
 * timeZone will be returned.
 *
 * If `showTime` is true, then the start of the minute in the configured
 * timeZone will be returned.
 */
export function DatePicker({
  value,
  onChange,
  earliestDate,
  latestDate,
  showTime,
  presetTitle,
  presetDates,
  timeZone,
  inputProps,
  isDisabled,
  isInvalid,
  isRequired,
}: DatePickerProps): React.ReactElement {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isMobile = useCurrentBreakpoint().isAtMost("sm");

  const popoverContentRef = useRef<HTMLDivElement>(null);

  const portalContainerRef = useOverlayPortalContainer();

  const triggerRef = useRef<HTMLInputElement>(null);

  const handleInputSelect = useCallback(
    (date: Date | undefined) => {
      const normalised = clampDate(date, earliestDate, latestDate);
      onChange(normalised);
    },
    [onChange, earliestDate, latestDate],
  );

  const handlePopoverChange = useCallback(
    (date: Date | undefined) => {
      const normalised = clampDate(date, earliestDate, latestDate);
      onChange(normalised);
      if (!showTime) {
        onClose();
      }
    },
    [showTime, onChange, onClose, earliestDate, latestDate],
  );

  const handlePopoverPresetSelect = useCallback(
    (date: Date | undefined) => {
      onChange(date);
      onClose();
    },
    [onChange, onClose],
  );

  useCloseWhenOutsideViewport({
    isOpen,
    targetRef: triggerRef,
    onClose,
  });

  // We don't use PopoverTrigger, as it will toggle the popover when you click
  // on it. In our case, we have a date input field as the trigger, and we don't
  // want the popover to toggle off when you click on it again.

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-start"
      autoFocus={false}
      returnFocusOnClose={false}
      closeOnBlur
      isLazy
      lazyBehavior="unmount"
      modifiers={[
        {
          // allow the popper to overlap the trigger button to stay inside the viewport
          name: "preventOverflow",
          options: {
            altAxis: true,
            tether: false,
            padding: isMobile ? 0 : 4,
          },
        },
      ]}
    >
      <PopoverAnchor>
        <DatePickerInput
          ref={triggerRef}
          value={value}
          timeZone={timeZone}
          onSelect={handleInputSelect}
          showTime={showTime}
          onFocus={onOpen}
          onBlur={() => {
            // clicking into the popover will not close it
            window.setTimeout(() => {
              const activeElement = document.activeElement;

              const focusIsInsidePopover =
                !!popoverContentRef.current &&
                !!activeElement &&
                popoverContentRef.current.contains(activeElement);

              if (!focusIsInsidePopover) {
                onClose();
              }
            }, 0);
          }}
          isDisabled={isDisabled}
          isRequired={isRequired}
          isInvalid={isInvalid}
          inputProps={inputProps}
        />
      </PopoverAnchor>
      <Portal containerRef={portalContainerRef}>
        <DatePickerPopoverContent
          ref={popoverContentRef}
          value={value}
          onChange={handlePopoverChange}
          onClose={onClose}
          onPresetSelect={handlePopoverPresetSelect}
          earliestDate={earliestDate}
          latestDate={latestDate}
          showTime={showTime}
          presetTitle={presetTitle}
          presetDates={presetDates}
          timeZone={timeZone}
        />
      </Portal>
    </Popover>
  );
}

// Clamp any selection to the earliest and latest dates allowed.
function clampDate(
  date: Date | undefined,
  min: Date | undefined,
  max: Date | undefined,
): Date | undefined {
  if (!date) {
    return undefined;
  }

  if (min && date < min) {
    return min;
  }

  if (max && date > max) {
    return max;
  }

  return date;
}
